import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Image as ImageIcon, Link as LinkIcon, X, Calendar, Plus, Type, CalendarDays } from 'lucide-react';
import { stickyColorFor, clientColorFor } from '../lib/stickyColors';

const STATUS_META = {
  draft: { label: 'draft', dot: '#94a3b8' },
  ready: { label: 'ready', dot: '#f59e0b' },
  scheduled: { label: 'scheduled', dot: '#a78bfa' },
  live: { label: 'live', dot: '#10b981' },
};

const PLATFORMS = { instagram: 'IG', tiktok: 'TT', ig_trial: 'Trial', linkedin: 'LI' };
const FEED_PLATFORM_FILTERS = { ...PLATFORMS, stories: 'Stories' };
const isStoryPost = (p) => Array.isArray(p.platforms) && p.platforms.includes('stories');
const sortStoriesByDate = (list) => list.slice().sort((a, b) => {
  if (!a.posting_date && !b.posting_date) return (a.position ?? 0) - (b.position ?? 0);
  if (!a.posting_date) return 1;
  if (!b.posting_date) return -1;
  if (a.posting_date === b.posting_date) return (a.position ?? 0) - (b.position ?? 0);
  return a.posting_date.localeCompare(b.posting_date);
});

const normFrame = (f) => {
  if (typeof f === 'string') return { url: f, text: '' };
  if (f && typeof f === 'object') return { url: f.url || '', text: f.text || '' };
  return { url: '', text: '' };
};

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const newChecklistId = () => 'chk_' + (window.crypto?.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2, 6));

// ------- Editable checklist (view-only link visitors can tick + add) -------
const PublicChecklist = ({ items, onChange, testPrefix }) => {
  const [draft, setDraft] = useState('');
  const addItem = () => {
    const text = draft.trim();
    if (!text) return;
    onChange([...items, { id: newChecklistId(), text, done: false }]);
    setDraft('');
  };
  const toggleItem = (id) => onChange(items.map((i) => i.id === id ? { ...i, done: !i.done } : i));
  const removeItem = (id) => onChange(items.filter((i) => i.id !== id));
  return (
    <div>
      {items.length > 0 && (
        <ul className="space-y-1 mb-1.5">
          {items.map((i) => (
            <li key={i.id} className="flex items-start gap-1.5 group" data-testid={`${testPrefix}-item-${i.id}`}>
              <input
                type="checkbox"
                checked={Boolean(i.done)}
                onChange={() => toggleItem(i.id)}
                className="mt-0.5 w-3 h-3 accent-lilac-500 cursor-pointer shrink-0"
                data-testid={`${testPrefix}-done-${i.id}`}
              />
              <span className={`flex-1 min-w-0 text-[11px] text-slate-600 break-words ${i.done ? 'line-through opacity-50' : ''}`}>{i.text}</span>
              <button
                type="button"
                onClick={() => removeItem(i.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 shrink-0"
                data-testid={`${testPrefix}-delete-${i.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-1">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }}
          placeholder="+ add note…"
          className="h-6 text-[11px] px-1.5 border border-lilac-200 rounded-md outline-none focus:ring-2 focus:ring-lilac-300 flex-1 bg-white"
          data-testid={`${testPrefix}-input`}
        />
        <button
          type="button"
          onClick={addItem}
          className="h-6 w-6 inline-flex items-center justify-center rounded-md bg-lilac-100 text-lilac-600 hover:bg-lilac-200 shrink-0"
          data-testid={`${testPrefix}-add`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ------- Feed Actions panel (right column, mirrors the app) -------
const PublicFeedActions = ({ conceptPosts, onOpenPost, onChangeChecklist }) => {
  return (
    <div className="w-full lg:w-80 shrink-0 bg-white border border-pink-100 rounded-2xl p-3" data-testid="public-feed-actions-panel">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Feed Actions</div>
      <div className="text-[10px] uppercase tracking-wide text-lilac-600 font-semibold mb-1.5">to plan · concept</div>
      {conceptPosts.length === 0 ? (
        <div className="text-xs text-slate-400 italic">no concept posts</div>
      ) : (
        <ul className="space-y-1.5">
          {conceptPosts.map((p) => (
            <li key={p.id} className="px-2 py-1.5 rounded-lg bg-lilac-50 border border-lilac-200" data-testid={`public-feed-actions-item-${p.id}`}>
              <button
                type="button"
                onClick={() => onOpenPost(p)}
                className="w-full flex items-center gap-2 text-left"
              >
                {p.cover_url ? (
                  <img src={p.cover_url} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                ) : (
                  <span className="w-7 h-7 rounded bg-lilac-100 flex items-center justify-center shrink-0"><Type className="w-3.5 h-3.5 text-lilac-500" /></span>
                )}
                <span className="flex-1 min-w-0 text-xs text-slate-700 truncate">{p.placeholder_text || p.pillar || 'untitled concept'}</span>
                {p.posting_date && <span className="text-[10px] text-lilac-700 font-medium shrink-0">{fmtShortDate(p.posting_date)}</span>}
              </button>
              <div className="mt-1.5 pl-1 border-l-2 border-lilac-200">
                <PublicChecklist
                  items={p.checklist_items || []}
                  onChange={(next) => onChangeChecklist(p.id, next)}
                  testPrefix={`public-feed-actions-checklist-${p.id}`}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ------- Story set row (shared by dedicated stories plans + feed-embedded story rows) -------
const PublicStoryRow = ({ post, onClick }) => {
  const s = STATUS_META[post.status] || STATUS_META.draft;
  const frames = (post.story_frames || []).map(normFrame);
  return (
    <div
      className="flex flex-col gap-2 border border-slate-200 rounded-xl p-2.5 cursor-pointer hover:border-pink-300 transition-colors"
      onClick={onClick}
      data-testid={`public-social-story-row-${post.id}`}
    >
      <span className={`text-sm font-semibold truncate ${post.title ? 'text-slate-800' : 'text-slate-400 italic font-normal'}`} data-testid={`public-social-story-title-${post.id}`}>
        {post.title || 'untitled story set'}
      </span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 overflow-x-auto">
          {frames.length === 0 ? (
            <div className="w-24 h-[170px] rounded-md bg-slate-100 flex items-center justify-center shrink-0"><ImageIcon className="w-6 h-6 text-slate-300" /></div>
          ) : (
            frames.map((f, i) => (
              <div key={i} className="w-24 h-[170px] rounded-md bg-slate-100 overflow-hidden shrink-0 ring-1 ring-slate-200">
                {f.url ? (
                  <img src={f.url} alt="" className="w-full h-full object-cover" />
                ) : f.text ? (
                  <div className="w-full h-full bg-lilac-50 flex items-center justify-center p-2"><span className="text-[11px] leading-tight text-lilac-600 font-medium text-center line-clamp-6 break-words">{f.text}</span></div>
                ) : null}
              </div>
            ))
          )}
        </div>
        <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">{frames.length} frame{frames.length === 1 ? '' : 's'}</span>
            {post.content_link && (
              <a
                href={post.content_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="open content link"
                className="text-pink-400 hover:text-pink-600"
                data-testid={`public-social-story-link-${post.id}`}
              >
                <LinkIcon className="w-3 h-3" />
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {post.posting_date && <span className="inline-flex items-center h-6 px-2 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-600 ring-1 ring-slate-200">{fmtDate(post.posting_date)}</span>}
            <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-700 ring-1 ring-slate-200">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} /> {s.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PostDetailModal = ({ post, onClose, swatch }) => {
  if (!post) return null;
  const s = STATUS_META[post.status] || STATUS_META.draft;
  const liveDate = fmtDate(post.posting_date);
  const isStory = Array.isArray(post.story_frames) && post.story_frames.length > 0;
  const postPlatforms = (post.platforms && post.platforms.length) ? post.platforms : ['instagram'];
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="public-social-post-modal"
    >
      <div
        className={`bg-white rounded-2xl overflow-hidden w-full max-h-[90vh] overflow-y-auto shadow-2xl ${isStory ? 'max-w-4xl' : 'max-w-2xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={isStory ? 'grid grid-cols-1' : 'grid grid-cols-1 sm:grid-cols-2'}>
          {isStory ? (
            <div className="bg-slate-50 flex items-center justify-center gap-3 p-6 overflow-x-auto">
              {post.story_frames.map(normFrame).map((f, i) => (
                <div key={i} className="w-48 aspect-[9/16] rounded-lg overflow-hidden ring-1 ring-slate-200 shrink-0">
                  {f.url ? (
                    <img src={f.url} alt="" className="w-full h-full object-cover" />
                  ) : f.text ? (
                    <div className="w-full h-full bg-lilac-50 flex items-center justify-center p-3"><span className="text-sm leading-tight text-lilac-600 font-medium text-center line-clamp-6 break-words">{f.text}</span></div>
                  ) : (
                    <div className="w-full h-full bg-slate-100" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="aspect-[4/5] bg-slate-100 flex items-center justify-center">
              {post.cover_url ? (
                <img src={post.cover_url} alt="" className="w-full h-full object-cover" />
              ) : post.placeholder_text ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-center gap-2 p-5" style={{ background: swatch.bg }}>
                  <span className="text-lg font-bold leading-snug" style={{ color: swatch.ink }}>{post.placeholder_text}</span>
                </div>
              ) : (
                <ImageIcon className="w-8 h-8 text-slate-300" />
              )}
            </div>
          )}
          <div className="p-5 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-1.5">
                {!isStory && postPlatforms.map((pl) => (
                  <span key={pl} className="inline-flex items-center h-5 px-1.5 rounded-full text-[9px] font-bold bg-slate-100 text-slate-600">{PLATFORMS[pl] || pl}</span>
                ))}
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700" data-testid="public-social-post-status">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />{s.label}
                </span>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="public-social-post-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {isStory && (
              <h2 className={`text-base font-semibold ${post.title ? 'text-slate-800' : 'text-slate-400 italic font-normal'}`} data-testid="public-social-post-title">
                {post.title || 'untitled story set'}
              </h2>
            )}

            {!isStory && post.pillar && (
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">pillar</p>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-pink-50 text-pink-700 border border-pink-100">{post.pillar}</span>
              </div>
            )}

            <div>
              <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> live date</p>
              <p className="text-sm text-slate-700" data-testid="public-social-post-live-date">{liveDate || '— not scheduled yet —'}</p>
            </div>

            {!isStory && (
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">caption</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="public-social-post-caption">{post.copy || '— no caption yet —'}</p>
              </div>
            )}

            {post.content_link && (
              <a
                href={post.content_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-pink-600 hover:text-pink-700"
                data-testid="public-social-post-content-link"
              >
                <LinkIcon className="w-3.5 h-3.5" /> open content link
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PUB_CAL_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PublicCalendarOverlay = ({ open, onClose, posts, onOpenPost }) => {
  const [cursor, setCursor] = useState(new Date());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  if (!open) return null;
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const monthLabel = cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const byDate = {};
  posts.forEach((p) => {
    const d = p.posting_date;
    if (!d) return;
    if (from && d < from) return;
    if (to && d > to) return;
    (byDate[d] = byDate[d] || []).push(p);
  });
  const first = new Date(y, m, 1);
  const startWeekday = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const iso = (d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const todayISO = new Date().toLocaleDateString('en-CA');
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="public-feed-calendar">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">feed calendar</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="public-feed-calendar-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100">‹</button>
            <span className="text-sm font-semibold w-36 text-center">{monthLabel}</span>
            <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100">›</button>
            <button onClick={() => setCursor(new Date())} className="ml-1 text-xs font-semibold text-pink-600 hover:text-pink-700">today</button>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-slate-400">dates</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-md" data-testid="public-feed-cal-from" />
            <span className="text-slate-400">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-md" data-testid="public-feed-cal-to" />
            {(from || to) && <button onClick={() => { setFrom(''); setTo(''); }} className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden mt-2">
          {PUB_CAL_WEEKDAYS.map((w) => <div key={w} className="bg-slate-50 text-[10px] uppercase text-slate-500 font-semibold text-center py-1">{w}</div>)}
          {cells.map((d, i) => (
            <div key={i} className={`min-h-[80px] p-1 ${d === null ? 'bg-slate-50' : 'bg-white'}`}>
              {d !== null && (
                <>
                  <div className={`text-[10px] font-semibold ${iso(d) === todayISO ? 'text-pink-600' : 'text-slate-400'}`}>{d}</div>
                  <div className="mt-0.5 space-y-0.5">
                    {(byDate[iso(d)] || []).map((p) => {
                      const st = STATUS_META[p.status] || STATUS_META.draft;
                      return (
                        <button key={p.id} onClick={() => { onOpenPost(p); onClose(); }} className="w-full flex items-center gap-1 text-left text-[9px] px-1 py-0.5 rounded bg-slate-50 hover:bg-pink-50">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.dot }} />
                          <span className="truncate">{p.placeholder_text || p.copy || 'post'}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PublicSocialFeed = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [activePost, setActivePost] = useState(null);
  const [platformFilter, setPlatformFilter] = useState(Object.keys(FEED_PLATFORM_FILTERS));
  const [highlightId, setHighlightId] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const tileRefs = useRef({});
  useEffect(() => {
    if (!highlightId) return;
    const clear = () => setHighlightId(null);
    const t = setTimeout(() => document.addEventListener('click', clear, { once: true }), 60);
    return () => { clearTimeout(t); document.removeEventListener('click', clear); };
  }, [highlightId]);

  useEffect(() => {
    api.get(`/public/social/${token}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [token]);

  const swatch = useMemo(() => stickyColorFor(clientColorFor(data?.client || {})), [data]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400" data-testid="public-social-error">this feed link is no longer valid</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">loading…</div>;

  const { client, plan, posts } = data;
  const isStories = plan.type === 'stories';
  const togglePlatform = (id) => setPlatformFilter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const conceptPosts = posts
    .filter((p) => p.status === 'concept')
    .filter((p) => (p.placeholder_text || '').trim() || (p.copy || '').trim() || (p.pillar || '').trim() || (p.checklist_items || []).length > 0 || p.cover_url)
    .slice()
    .sort((a, b) => {
      if (!a.posting_date && !b.posting_date) return 0;
      if (!a.posting_date) return 1;
      if (!b.posting_date) return -1;
      return a.posting_date.localeCompare(b.posting_date);
    });
  const saveChecklist = async (postId, items) => {
    setData((prev) => prev ? { ...prev, posts: prev.posts.map((p) => p.id === postId ? { ...p, checklist_items: items } : p) } : prev);
    try { await api.patch(`/public/social/${token}/posts/${postId}/checklist`, { checklist_items: items }); } catch { /* keep optimistic */ }
  };
  const visiblePosts = posts.filter((p) => {
    if (isStories) return true;
    const postPlatforms = (p.platforms && p.platforms.length) ? p.platforms : ['instagram'];
    return postPlatforms.some((pl) => platformFilter.includes(pl));
  });
  const feedGridTiles = isStories ? [] : visiblePosts.filter((p) => !isStoryPost(p));
  const feedStoryRows = isStories ? sortStoriesByDate(visiblePosts) : sortStoriesByDate(visiblePosts.filter(isStoryPost));
  const onlyStoriesSelected = !isStories && platformFilter.length === 1 && platformFilter[0] === 'stories';

  const scrollToToday = () => {
    const todayISO = new Date().toLocaleDateString('en-CA');
    const dated = feedGridTiles.filter((p) => p.posting_date);
    if (!dated.length) return;
    let target = dated.find((p) => p.posting_date === todayISO);
    if (!target) {
      target = dated.reduce((best, p) => {
        const diff = Math.abs(new Date(p.posting_date).getTime() - new Date(todayISO).getTime());
        return (!best || diff < best.diff) ? { p, diff } : best;
      }, null)?.p;
    }
    if (target && tileRefs.current[target.id]) {
      tileRefs.current[target.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(target.id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-lilac-50 p-6 lg:p-10" data-testid="public-social-page">
      <div className="max-w-[1440px] mx-auto">
        <header className="flex items-center gap-3 mb-8">
          <span className="w-7 h-7 rounded-full ring-2 ring-white shadow-sm shrink-0" style={{ background: swatch.tape }} data-testid="public-social-client-dot" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{client.name}</h1>
            <p className="text-sm text-slate-500">{plan.name} · {isStories ? 'stories plan' : 'feed plan'} · {visiblePosts.length} {isStories ? 'set' : 'post'}{visiblePosts.length === 1 ? '' : 's'}{visiblePosts.length !== posts.length ? ` of ${posts.length}` : ''} · view-only</p>
          </div>
          <div className="flex-1" />
          {plan.content_drive_link && (
            <a
              href={plan.content_drive_link.startsWith('http') ? plan.content_drive_link : `https://${plan.content_drive_link}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium bg-pink-50 border border-pink-200 text-pink-700 hover:bg-pink-100"
              data-testid="public-social-content-drive"
            >
              <LinkIcon className="w-3.5 h-3.5" /> content drive
            </a>
          )}
          {!isStories && (
            <div className="flex items-center gap-1.5">
              <button onClick={scrollToToday} className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold bg-pink-500 text-white hover:bg-pink-600" data-testid="public-feed-today-btn">
                <CalendarDays className="w-3.5 h-3.5" /> today
              </button>
              <button onClick={() => setCalendarOpen(true)} className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold bg-white text-slate-600 border border-slate-200 hover:border-pink-300" data-testid="public-feed-calendar-btn">
                <CalendarDays className="w-3.5 h-3.5" /> calendar
              </button>
            </div>
          )}
          {!isStories && (
            <div className="flex items-center gap-1" data-testid="public-social-platform-filter">
              {Object.entries(FEED_PLATFORM_FILTERS).map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => togglePlatform(id)}
                  className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${platformFilter.includes(id) ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300'}`}
                  data-testid={`public-social-platform-filter-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </header>

        {plan.key_message && plan.key_message.trim() && (
          <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 mb-6" data-testid="public-key-message">
            <div className="text-[11px] uppercase tracking-widest text-pink-600 font-semibold mb-1">key message</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{plan.key_message}</div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4 items-start">
        <div className="flex-1 w-full min-w-0 bg-white border border-pink-100 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-pink-50 border-b border-pink-100 text-xs font-semibold text-pink-700 uppercase tracking-widest">{isStories ? 'story sets · tap a set for details' : 'feed preview · tap a post for details'}</div>
          {isStories ? (
            feedStoryRows.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400">no story sets for this plan</div>
            ) : (
              <div className="p-3 space-y-2" data-testid="public-social-story-list">
                {feedStoryRows.map((p) => (
                  <PublicStoryRow key={p.id} post={p} onClick={() => setActivePost(p)} />
                ))}
              </div>
            )
          ) : feedGridTiles.length === 0 && feedStoryRows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">no posts for this feed</div>
          ) : (
            <div className="p-1">
              {onlyStoriesSelected && feedStoryRows.length > 0 && (
                <div className="space-y-2 mb-1 px-1" data-testid="public-social-story-rows-top">
                  {feedStoryRows.map((p) => (
                    <PublicStoryRow key={p.id} post={p} onClick={() => setActivePost(p)} />
                  ))}
                </div>
              )}
              {feedGridTiles.length > 0 && (
                <div className="grid grid-cols-3 gap-0.5 sm:gap-1">
                  {feedGridTiles.map((p) => {
                const s = STATUS_META[p.status] || STATUS_META.draft;
                const postPlatforms = (p.platforms && p.platforms.length) ? p.platforms : ['instagram'];
                return (
                  <div
                    key={p.id}
                    ref={(el) => { tileRefs.current[p.id] = el; }}
                    className={`relative aspect-[4/5] bg-slate-100 rounded-md overflow-hidden ring-1 group cursor-pointer transition-all ${highlightId === p.id ? 'ring-4 ring-pink-400' : 'ring-slate-200 hover:ring-pink-300'}`}
                    onClick={() => setActivePost(p)}
                    data-testid={`public-social-tile-${p.id}`}
                  >
                    {p.cover_url ? (
                      <img src={p.cover_url} alt="" className="w-full h-full object-cover" />
                    ) : p.placeholder_text ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-center gap-1 p-3" style={{ background: swatch.bg }}>
                        <span className="text-sm font-bold leading-snug line-clamp-5 break-words" style={{ color: swatch.ink }}>{p.placeholder_text}</span>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon className="w-5 h-5" /></div>
                    )}
                    <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5">
                      {postPlatforms.map((pl) => (
                        <span key={pl} className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full text-[9px] font-bold bg-white/90 backdrop-blur text-slate-600">
                          {PLATFORMS[pl] || pl}
                        </span>
                      ))}
                    </div>
                    <div className="absolute top-1.5 right-1.5">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-white/90 backdrop-blur text-slate-600">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />{s.label}
                      </span>
                    </div>
                    {p.content_link && (
                      <a
                        href={p.content_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="open content link"
                        className="absolute bottom-1.5 right-1.5 w-6 h-6 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur text-pink-500 hover:bg-white hover:text-pink-600 shadow"
                        data-testid={`public-social-tile-link-${p.id}`}
                      >
                        <LinkIcon className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                );
                  })}
                </div>
              )}
              {!onlyStoriesSelected && feedStoryRows.length > 0 && (
                <div className={`space-y-2 px-1 ${feedGridTiles.length > 0 ? 'mt-3 pt-3 border-t border-slate-100' : ''}`} data-testid="public-social-story-rows-bottom">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">stories</div>
                  {feedStoryRows.map((p) => (
                    <PublicStoryRow key={p.id} post={p} onClick={() => setActivePost(p)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <PublicFeedActions
          conceptPosts={conceptPosts}
          onOpenPost={setActivePost}
          onChangeChecklist={saveChecklist}
        />
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-8">shared feed plan · lve social</p>
      </div>

      <PostDetailModal post={activePost} onClose={() => setActivePost(null)} swatch={swatch} />
      <PublicCalendarOverlay open={calendarOpen} onClose={() => setCalendarOpen(false)} posts={feedGridTiles} onOpenPost={setActivePost} />
    </div>
  );
};

export default PublicSocialFeed;
